import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { BankConnectionList } from "Components/Shared/BankConnectionList";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { useBanksEnvironment } from "Api/Queries/Banks/useBanksEnvironments";
import { useBankingAuth } from "Hooks/useBankingAuth";
import type { BankEnvironment } from "Infrastructure/Api/Api";
import { useSearchParams } from "Navigation/useSearchParams";
import { resetTransactionListPagination } from "State/Transactions/TransactionReducer";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

const PageResources = Resources.Banking.Auth;

export const BankingAuthPage = () => {
  const dispatch = useDispatch();
  const { t } = useResource();
  const [searchParams] = useSearchParams();

  const { isPending, authorize, selectedEnvironment } = useBankingAuth();
  const { data: banks } = useBanksEnvironment();
  const hasAutoStartedRef = useRef(false);

  const requestedEnvironment = searchParams.get("environment");

  useEffect(() => {
    return () => {
      dispatch(resetTransactionListPagination());
    };
  }, [dispatch]);

  // Deep link from the Orbit L checklist: skip the picker and go straight to the bank the merchant
  // already uses. Anything the picker would not offer falls back to the picker instead of failing.
  useEffect(() => {
    if (hasAutoStartedRef.current || !requestedEnvironment || !banks) {
      return;
    }

    const offeredBank = banks.items.find(
      ({ bankEnvironment, isVisible, isEnabled }) =>
        bankEnvironment === requestedEnvironment && isVisible && isEnabled,
    );

    if (!offeredBank) {
      return;
    }

    hasAutoStartedRef.current = true;
    authorize({ environment: offeredBank.bankEnvironment });
  }, [banks, requestedEnvironment, authorize]);

  const onBankSelected = (environment: BankEnvironment) => {
    if (isPending) {
      return;
    }

    authorize({ environment });
  };

  return (
    <AuthenticatedLayout
      title={t(PageResources.Title)}
      backNavigation={{
        mode: "historyStack",
        fallbackUrl: appUrl("bank-address-list"),
      }}
    >
      <StyledPageWrapper>
        <PrimaryPaper>
          <BankConnectionList
            isPending={isPending}
            onBankSelected={onBankSelected}
            selectedEnvironment={selectedEnvironment}
          />
        </PrimaryPaper>
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default BankingAuthPage;
